import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import {
  PrivateChatA2AApiProvider,
  usePrivateChatA2AApi,
} from '../Services/A2AKnowledgeApiProvider';
import useApiService from '../Services/ApiService';
import CONFIG from '../Services/CONFIG';
import { useAlert } from './AlertContext';
import { ConfirmationProvider, useConfirmation } from './ConfirmationContext';
import { CustomInput } from './CustomFormElements';
import './KnowledgeChatLauncher.css';
import KnowledgeChatLoader from './KnowledgeChatLoader';

// ---- helpers для A2A стріму (адаптація з TestAgentModal) ----
// tolerant text resolver for mixed shapes
const getMessageText = (item) => {
  if (!item) return '';
  if (typeof item === 'string') return item;
  if (Array.isArray(item?.parts)) {
    return item.parts
      .map((p) => p?.text || '')
      .filter(Boolean)
      .join('\n');
  }
  return item?.rawText || item?.text || '';
};

// String/escape-aware extraction of full JSON objects from a text buffer
function extractJsonObjects(text) {
  const out = [];
  let start = -1,
    depth = 0,
    inStr = false,
    esc = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inStr) {
      if (esc) {
        esc = false;
      } else if (ch === '\\') {
        esc = true;
      } else if (ch === '"') {
        inStr = false;
      }
      continue;
    }

    if (ch === '"') {
      inStr = true;
      if (depth === 0 && start === -1) start = i;
      continue;
    }
    if (ch === '{') {
      if (depth === 0) start = i;
      depth++;
      continue;
    }
    if (ch === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        const candidate = text.slice(start, i + 1);
        try {
          out.push(JSON.parse(candidate));
        } catch {}
        start = -1;
      }
      continue;
    }
  }
  return out;
}

const KnowledgeChat = () => {
  const { get, post, patch, del } = useApiService(CONFIG.SERVER_ORGANIZATION);
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { sendMessage: sendA2AMessage } = usePrivateChatA2AApi();

  const [chats, setChats] = useState([]);

  const [activeChatId, setActiveChatId] = useState(null);
  const [activeChatKey, setActiveChatKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [editingChatId, setEditingChatId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [dropdownTop, setDropdownTop] = useState(0);

  const refLoadMessage = useRef(null);
  const [isLoadMessageVisible, setIsLoadMessageVisible] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const limit = 30;
  const chatBoxRef = useRef(null);

  const getChats = useCallback(async () => {
    try {
      const response = await get('/v1/private_chats');

      if (response.status >= 200 && response.status < 300) {
        if (response.data) {
          return response.data;
        } else {
          return [];
        }
      } else {
        throw new Error(`Failed to fetch chats: ${response.data?.message || 'Unknown error'}`);
      }
    } catch (error) {
      showAlert('error', 'An error occurred while loading chats. Please try again.');
      throw error;
    }
  }, [get]);

  const getMessages = useCallback(
    async (chat_key, skipValue = 0) => {
      try {
        const chatBox = chatBoxRef.current;

        let previousScrollHeight = 0;
        if (chatBox) {
          previousScrollHeight = chatBox.scrollHeight;
        }
        const response = await get(
          `/v1/private_chats/${chat_key}/messages?skip=${skipValue}&limit=${limit}`,
        );

        if (response.status >= 200 && response.status < 300) {
          if (response.data && Array.isArray(response.data.items)) {
            const sortedMessages = response.data.items.reverse();

            setMessages((prev) => [...sortedMessages, ...prev]);

            if (response.data.totalCount < skipValue + limit) {
              setHasMore(false);
            } else {
              setHasMore(true);
            }
            setTimeout(() => {
              if (chatBox) {
                const newScrollHeight = chatBox.scrollHeight;
                chatBox.scrollTop = newScrollHeight - previousScrollHeight;
              }
            }, 0);
          } else {
            setMessages([]);
          }
        } else {
          setMessages([]);
        }
      } catch (error) {
        setMessages([]);
      }
    },
    [get, setMessages],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsLoadMessageVisible(entry.isIntersecting);
    });

    if (refLoadMessage.current) {
      observer.observe(refLoadMessage.current);
    }

    return () => {
      if (refLoadMessage.current) {
        observer.unobserve(refLoadMessage.current);
      }
    };
  }, [refLoadMessage]);

  useEffect(() => {
    if (isLoadMessageVisible) {
      const newSkip = skip + limit;
      if (hasMore && activeChatKey) {
        getMessages(activeChatKey, newSkip);
        setSkip(newSkip);
      }
    }
  }, [isLoadMessageVisible]);

  const updateChatIsPinned = useCallback(
    async (chat_key, new_is_pinned_status) => {
      try {
        const response = await patch(`/v1/private_chats/${chat_key}`, {
          is_pinned: new_is_pinned_status,
        });

        if (response.status >= 200 && response.status < 300) {
          setChats((prevChats) =>
            prevChats.map((chat) =>
              chat.key === chat_key ? { ...chat, is_pinned: new_is_pinned_status } : chat,
            ),
          );
        }
        console.log('status', response.status);
      } catch (error) {
        console.error('Error updating chat pin status:', error);
      }
    },
    [patch, setChats],
  );

  const updateChatName = useCallback(
    async (chat_key, new_name) => {
      try {
        const response = await patch(`/v1/private_chats/${chat_key}`, { chat_name: new_name });

        if (response.status >= 200 && response.status < 300) {
          setChats((prevChats) =>
            prevChats.map((chat) =>
              chat.key === chat_key ? { ...chat, chat_name: new_name } : chat,
            ),
          );
        }
      } catch (error) {
        console.error('Error updating chat name:', error);
      }
    },
    [patch, setChats],
  );

  const deleteChat = useCallback(
    async (chat_key) => {
      try {
        const response = await del(`/v1/private_chats/${chat_key}`);

        if (response.status >= 200 && response.status < 300) {
          setChats((prevChats) => prevChats.filter((chat) => chat.key !== chat_key));
          setActiveChatId(null);
          setActiveChatKey(null);
          setMessages([]);
        }
      } catch (error) {
        console.error('Error deleting chat:', error);
      }
    },
    [del, setChats, setActiveChatId, setActiveChatKey, setMessages],
  );

  const createNewChat = useCallback(
    async (chat_name) => {
      try {
        const response = await post('/v1/private_chats', { chat_name });

        if (response.status >= 200 && response.status < 300) {
          const newChat = response.data;
          if (newChat && newChat.id && newChat.key) {
            setChats((prevChats) => [...prevChats, newChat]);
            setActiveChatId(newChat.id);
            setActiveChatKey(newChat.key);
          }
        }
      } catch (error) {}
    },
    [post, setChats, setActiveChatId, setActiveChatKey],
  );

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const data = await getChats();
        setChats(data.items);
        if (data.items.length > 0) {
          setActiveChatId(data.items[0].id);
          setActiveChatKey(data.items[0].key);
        }
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchChats();
  }, [getChats]);

  useEffect(() => {
    if (activeChatKey) {
      getMessages(activeChatKey);
    }
  }, [activeChatKey, getMessages]);

  useEffect(() => {
    const chatListEl = document.querySelector('.chat-list');
    if (chatListEl) {
      chatListEl.scrollTop = chatListEl.scrollHeight;
    }
  }, [chats.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSend = async () => {
    if (!input.trim() || !activeChatId || !activeChatKey) {
      return;
    }
    setIsProcessing(true);

    const currentInput = input;
    setInput('');

    const userMessage = {
      message_sender: 'USER',
      message_text: currentInput,
      timestamp: new Date().toISOString(),
    };

    const processingMessage = {
      message_sender: 'ASSISTANT',
      message_text: '*processing...*',
      isLoader: true,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage, processingMessage]);

    // callback для стріму A2A
    const eventCallback = (eventMessage) => {
      try {
        const candidateEvents = [];
        if (typeof eventMessage === 'string') {
          const objs = extractJsonObjects(eventMessage);
          if (objs.length) candidateEvents.push(...objs);
        } else if (eventMessage && typeof eventMessage === 'object') {
          candidateEvents.push(eventMessage);
        }

        for (const ev of candidateEvents) {
          const statusEnvelope = ev?.result?.status ? ev.result : null;

          if (statusEnvelope) {
            if (statusEnvelope.status.state === 'submitted') continue;

            let messageText = getMessageText(statusEnvelope.status.message);
            if (!messageText) continue;

            const botMessage = {
              message_sender: 'ASSISTANT',
              message_text: messageText,
              timestamp: statusEnvelope.status.timestamp || new Date().toISOString(),
            };

            setMessages((prev) => {
              const withoutLoaders = prev.filter((m) => !m.isLoader);
              return [...withoutLoaders, botMessage, processingMessage];
            });
          }
        }
      } catch (e) {
        // ігноруємо биті чанки
      }
    };

    try {
      // історію зараз бекенд бере з БД (через Preset), тому conversation можна не передавати
      await sendA2AMessage(activeChatKey, currentInput, [], [], eventCallback);

      // прибрати всі лоадери по завершенню
      setMessages((prev) => prev.filter((m) => !m.isLoader));
    } catch (error) {
      console.error('Error sending message via A2A:', error);
      showAlert('fail', 'Failed to send message. Please try again.');
      setMessages((prev) =>
        prev
          .filter((m) => !m.isLoader)
          .concat([
            {
              message_sender: 'ASSISTANT',
              message_text: `⚠️ Error: ${error?.message || 'Failed to send message.'}`,
              timestamp: new Date().toISOString(),
            },
          ]),
      );
    } finally {
      setIsProcessing(false);
    }

    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChatClick = (chat) => {
    if (activeChatId !== chat.id) {
      setMessages([]);
      setActiveChatId(chat.id);
      setActiveChatKey(chat.key);
      setOpenDropdownId(null);
      setSkip(0);
    }
  };

  const handleNewChat = async () => {
    await createNewChat('New Chat');
    setMessages([]);
    setSkip(0);
  };

  const handleRename = async (id, name) => {
    const forbiddenChars = /[<>\/\\{}[\]^`|]/;
    if (forbiddenChars.test(name.trim())) {
      showAlert(
        'fail',
        'Invalid chat name. Make sure to avoid using special symbols like < > / \\ { }',
      );
      return;
    }
    const chatToRename = chats.find((c) => c.id === id);
    await updateChatName(chatToRename.key, name);
    setEditingChatId(null);
  };

  const handleDelete = async (id) => {
    showConfirmation('Are you sure you want to delete this chat?', () => {
      const chatToDelete = chats.find((c) => c.id === id);
      deleteChat(chatToDelete.key);
      setOpenDropdownId(null);
    });
  };

  const handlePin = async (chatId) => {
    const chatToPin = chats.find((c) => c.id === chatId);
    console.log('hand pin', chatToPin);

    if (!chatToPin) return;
    const newPinnedStatus = !chatToPin.is_pinned;
    await updateChatIsPinned(chatToPin.key, newPinnedStatus);
    setOpenDropdownId(null);
  };

  const pinnedChats = chats.filter((c) => c.is_pinned);
  const otherChats = chats.filter((c) => !c.is_pinned);

  return (
    <div className="knowledge-chat-container d-flex">
      <div className="sidebar">
        {isLoading ? (
          <KnowledgeChatLoader />
        ) : (
          <div className="chat-list">
            <div className="search">
              <div className="search-wrapper">
                <div className="icon-wrapper">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </div>
                <input className="search-input" type="text" />
              </div>
            </div>
            {/* Pinned Chats */}
            {pinnedChats.length > 0 && (
              <div className="pinned-chats">
                <div className="pinned-label">Pinned</div>
                {pinnedChats.map((chat) => (
                  <div
                    onClick={() => handleChatClick(chat)}
                    key={chat.id}
                    className={`chat-item ${chat.id === activeChatId ? 'active' : ''}`}
                  >
                    {editingChatId === chat.id ? (
                      <input
                        className="rename-input"
                        autoFocus
                        defaultValue={chat.chat_name}
                        onBlur={(e) => handleRename(chat.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleRename(chat.id, e.target.value);
                          } else if (e.key === 'Escape') {
                            e.target.value = chat.chat_name;
                            setEditingChatId(null);
                          }
                        }}
                      />
                    ) : (
                      <>
                        <div>{chat.chat_name}</div>
                        <button
                          className="chat-more-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            const container = document.querySelector('.knowledge-chat-container');
                            const containerRect = container.getBoundingClientRect();
                            const buttonRect = e.currentTarget.getBoundingClientRect();
                            const topOffset = buttonRect.top - containerRect.top;
                            setDropdownTop(topOffset + e.currentTarget.offsetHeight);
                            setOpenDropdownId(openDropdownId === chat.id ? null : chat.id);
                          }}
                        >
                          ⋮
                        </button>
                        {openDropdownId === chat.id && (
                          <div
                            className="chat-dropdown"
                            ref={dropdownRef}
                            style={{
                              top: `${dropdownTop}px`,
                              left: '160px',
                              position: 'absolute',
                            }}
                          >
                            <div
                              className="menu-item"
                              onClick={() => {
                                setEditingChatId(chat.id);
                                setOpenDropdownId(null);
                              }}
                            >
                              Rename
                            </div>
                            <div className="menu-item" onClick={() => handleDelete(chat.id)}>
                              Delete
                            </div>
                            <div className="menu-item" onClick={() => handlePin(chat.id)}>
                              {chat.is_pinned ? 'Unpin' : 'Pin'}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* All (Unpinned) Chats */}
            <div className="all-chats">
              <div className="all-label">Chats</div>
              {otherChats.map((chat) => (
                <div
                  onClick={() => handleChatClick(chat)}
                  key={chat.id}
                  className={`chat-item ${chat.id === activeChatId ? 'active' : ''}`}
                >
                  {editingChatId === chat.id ? (
                    <input
                      className="rename-input"
                      autoFocus
                      defaultValue={chat.chat_name}
                      onBlur={(e) => handleRename(chat.id, e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRename(chat.id, e.target.value)}
                    />
                  ) : (
                    <>
                      <div>{chat.chat_name}</div>
                      <button
                        className="chat-more-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const container = document.querySelector('.knowledge-chat-container');
                          const containerRect = container.getBoundingClientRect();
                          const buttonRect = e.currentTarget.getBoundingClientRect();
                          const topOffset = buttonRect.top - containerRect.top;
                          setDropdownTop(topOffset + e.currentTarget.offsetHeight);
                          setOpenDropdownId(openDropdownId === chat.id ? null : chat.id);
                        }}
                      >
                        ⋮
                      </button>
                      {openDropdownId === chat.id && (
                        <div
                          className="chat-dropdown"
                          ref={dropdownRef}
                          style={{
                            top: `${dropdownTop}px`,
                            left: '210px',
                            position: 'absolute',
                          }}
                        >
                          <div
                            className="menu-item"
                            onClick={() => {
                              setEditingChatId(chat.id);
                              setOpenDropdownId(null);
                            }}
                          >
                            Rename
                          </div>
                          <div className="menu-item" onClick={() => handleDelete(chat.id)}>
                            Delete
                          </div>
                          <div className="menu-item" onClick={() => handlePin(chat.id)}>
                            {chat.is_pinned ? 'Unpin' : 'Pin'}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {!isLoading && (
          <div className="new-chat-button-container">
            <button className="new-chat-button" onClick={handleNewChat}>
              <i className="fa-solid fa-plus"></i>
            </button>
          </div>
        )}
      </div>

      <div className="chat-panel radius-yes flex-1">
        <div className="chat-box radius-yes p-2">
          <div className="chat-box-knowledge-chat" ref={chatBoxRef}>
            <div ref={refLoadMessage}></div>
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`message ${msg.message_sender === 'USER' ? 'user' : 'assistant'}`}
              >
                <div className="test-message-content">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      a: ({ node, ...props }) => (
                        <a {...props} target="_blank" rel="noopener noreferrer">
                          {props.children}
                        </a>
                      ),
                    }}
                  >
                    {msg.message_text}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </div>
        <div className="input-row">
          <CustomInput
            id="chat_input"
            disabled={isProcessing}
            label=""
            placeholder="Type your message..."
            value={input}
            type="text"
            onChange={(id, val) => setInput(val)}
            onKeyDown={(e) => e.key === 'Enter' && handleSend()}
          />
          <button className="test-agent-submit" onClick={handleSend}>
            <i className="fa-solid fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

const WrappedKnowledgeChat = () => (
  <ConfirmationProvider>
    <PrivateChatA2AApiProvider>
      <KnowledgeChat />
    </PrivateChatA2AApiProvider>
  </ConfirmationProvider>
);

export default WrappedKnowledgeChat;
